import { computed, ref, watch, type Ref, provide } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useLoadListItem } from '@/entities/Inventory/ListItem/model/api'
import { useLoadInventObjectType } from '@/entities/Inventory/ObjectTypes'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import {
  useLoadObjectPassportFields,
  useSaveObjectPassportFields,
} from '@/entities/Inventory/ObjectPassportFields'
import { useParentAccordions } from '@/features/Inventory/PassportBuilderV2/model/useParentAccordions'
import {
  useSendObjectList,
  buildObjectPassportSavePayload,
} from '@/features/Inventory/ObjectListEdit/model/api'
import {
  cloneObjectPassportSnapshot,
  createEmptyObjectPassportElement,
  hasObjectPassportChanges,
  mergeLoadedObjectPassportElement,
  type ObjectPassportElement,
} from '@/features/Inventory/ObjectListEdit/model/model'
import {
  createRuntimeFieldValueMap,
  type RuntimeFieldValueMap,
} from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import { buildRuntimeLayout } from './runtimeLayoutAdapter'
import { resolveRuntimeFieldGroups } from './resolveRuntimeFieldGroups'
import {
  objectPassportFieldGroupsToRuntimeGroups,
  objectPassportFieldsToGridTemplate,
} from './objectPassportFieldsGridTemplate'
import {
  buildLinkedPassportDisplayMapForRuntime,
  collectLinkedSourceFieldIds,
} from './resolveLinkedPassportValues'
import { usePassportPseudosqlRecompute } from './usePassportPseudosqlRecompute'
import {
  PENDING_BINDINGS_KEY,
  usePendingTableBindings,
} from './usePendingTableBindings'
import {
  PENDING_FIELD_LINKS_KEY,
  usePendingFieldLinks,
} from '@/entities/Inventory/ObjectPassportFields/model/usePendingFieldLinks'
import { syntheticParentObjectNameInventoryField } from './parentObjectNameGrid'
import { syntheticListCatalogNameInventoryField } from './listCatalogNameGrid'
import { filterRuntimeOwnCatalogFields, parentAccordionFieldIdSet } from './runtimeOwnCatalogFields'
import { mergeFieldMetadata } from './mergeFieldMetadata'
import {
  shouldUseNormalizedObjectPassport,
  objectPassportFieldsToInventoryFields,
} from '@/entities/Inventory/ObjectPassportFields/model/objectPassportFieldAdapter'
import {
  buildObjectPassportFieldValuesPayload,
  buildRuntimeFieldValueMapFromObjectPassport,
} from './objectPassportRuntimeAdapter'
import {
  collectRuntimeParentAccordionRoots,
  runtimeParentAccordionInputsFingerprint,
} from './runtimeParentAccordionRoots'

type RuntimeFilter = {
  status_date: null | string
}

type ObjectTypeRuntimeConfig = {
  passport_grid_template?: unknown[]
  /** Some APIs use `field_groups`; passport list payload often uses `groups` on `invent_object_type`. */
  field_groups?: unknown[]
  groups?: unknown[]
  fields?: ObjectInventoryField[]
}

function isExplicitVerDate(verDate: string | undefined): verDate is string {
  return verDate != null && String(verDate).length > 0
}

type FieldValueEntry = { field_id: number; value: string | null; value_list?: number[] | null }

/**
 * Объединяет значения полей с патчами связей: для каждого field_id из overlay
 * удаляется строка из base, затем overlay добавляется (связь управляется value_list).
 */
function mergeFieldLinkPatches(base: FieldValueEntry[], overlay: FieldValueEntry[]): FieldValueEntry[] {
  const overlayIds = new Set(overlay.map((o) => o.field_id))
  return [...base.filter((b) => !overlayIds.has(b.field_id)), ...overlay]
}

function toRuntimeFieldArray(
  value: ObjectPassportElement['fields'],
): Array<{ id: string | number; value?: unknown }> {
  if (Array.isArray(value)) return value
  return Object.entries(value ?? {}).map(([id, fieldValue]) => ({ id, value: fieldValue }))
}

/** Passport list item embeds layout on `invent_object_type`; object-type GET may omit `passport_grid_template`. Prefer the element's template because it contains runtime-specific element-link bindings (`link_field_id`, `inventory_field_id`) that the generic object-type template strips. */
function passportGridTemplateForRuntime(
  fromObjectType: ObjectTypeRuntimeConfig,
  element: ObjectPassportElement,
): unknown[] | undefined {
  const q = fromObjectType.passport_grid_template
  const e = (element.invent_object_type as ObjectTypeRuntimeConfig).passport_grid_template
  if (Array.isArray(e) && e.length > 0) return e
  if (Array.isArray(q) && q.length > 0) return q
  if (Array.isArray(e)) return e
  if (Array.isArray(q)) return q
  return undefined
}

export function usePassportRuntimeV2(
  elementId: Ref<number>,
  objectId: Ref<string>,
  filter: Ref<RuntimeFilter> = ref({ status_date: null }),
) {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()
  const elementQuery = useLoadListItem(elementId, filter)
  const passportFieldsQuery = useLoadObjectPassportFields(elementId, {
    statusDate: computed(() => filter.value.status_date),
  })
  const element = ref<ObjectPassportElement>(createEmptyObjectPassportElement())
  const fieldValues = ref<RuntimeFieldValueMap>({})
  const initialSnapshot = ref<ReturnType<typeof cloneObjectPassportSnapshot> | null>(null)

  const objectTypeId = computed(
    () =>
      Number(element.value.invent_objects_type_id) ||
      Number(element.value.invent_object_type?.id) ||
      0,
  )
  const objectTypeQuery = useLoadInventObjectType(objectTypeId, objectId)

  const paletteQuery = useQuery({
    queryKey: computed(() => [
      'load_type_passport_grid_field',
      String(objectTypeId.value),
      objectId.value,
    ]),
    queryFn: () =>
      http.get(`/inventory/group/${objectId.value}/type-field/${Number(objectTypeId.value)}`),
    enabled: computed(() => {
      if (!objectTypeId.value || !objectId.value) return false
      if (shouldUseNormalizedObjectPassport(passportFieldsQuery.data.value)) return false
      return true
    }),
    retry: false,
  })

  const objectTypeConfig = computed(
    () => (objectTypeQuery.data.value?.data ?? {}) as ObjectTypeRuntimeConfig,
  )

  const normalizedPassportPayload = computed(() => passportFieldsQuery.data.value ?? null)

  const useNormalizedPassport = computed(() =>
    shouldUseNormalizedObjectPassport(normalizedPassportPayload.value),
  )

  const normalizedOwnFieldMetadata = computed(() => {
    const payload = normalizedPassportPayload.value
    if (!shouldUseNormalizedObjectPassport(payload)) return null
    return objectPassportFieldsToInventoryFields(payload!.fields)
  })

  const fieldGroupsRef = computed<ObjectTypeGroup[]>(() => {
    const normalized = normalizedPassportPayload.value
    if (useNormalizedPassport.value) {
      return objectPassportFieldGroupsToRuntimeGroups(normalized?.fieldGroups ?? [])
    }
    const raw = resolveRuntimeFieldGroups(
      objectTypeConfig.value,
      element.value.invent_object_type as ObjectTypeRuntimeConfig,
    )
    return Array.isArray(raw) ? (raw as ObjectTypeGroup[]) : []
  })

  const catalogFieldsForParentAccordions = computed(() => {
    const fromApi = objectTypeConfig.value.fields
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi as ObjectInventoryField[]
    return (element.value.invent_object_type?.fields ?? []) as ObjectInventoryField[]
  })

  const passportGridTemplateRaw = computed(
    () =>
      (passportGridTemplateForRuntime(objectTypeConfig.value, element.value) ??
        []) as GridFieldType[],
  )

  const passportGridTemplateRef = computed(() => {
    if (useNormalizedPassport.value && normalizedPassportPayload.value) {
      return objectPassportFieldsToGridTemplate(normalizedPassportPayload.value.fields)
    }
    return passportGridTemplateRaw.value
  })

  const listTypesRootsForAccordions = computed(() =>
    collectRuntimeParentAccordionRoots(
      passportGridTemplateRef.value,
      catalogFieldsForParentAccordions.value,
      objectTypeId.value,
    ),
  )

  const parentAccordionInputsFingerprint = computed(() =>
    runtimeParentAccordionInputsFingerprint(
      catalogFieldsForParentAccordions.value,
      objectId.value,
      objectTypeId.value,
      fieldGroupsRef.value,
      passportGridTemplateRef.value,
    ),
  )

  const needsParentAccordionMetadata = computed(
    () => !useNormalizedPassport.value && listTypesRootsForAccordions.value.length > 0,
  )

  const parentAccordionsEnabled = computed(() => needsParentAccordionMetadata.value)

  const { parentAccordions, isLoading: parentAccordionsLoading } = useParentAccordions(
    catalogFieldsForParentAccordions,
    objectId,
    objectTypeId,
    fieldGroupsRef,
    {
      disableNestedSkip: true,
      listTypesRoots: listTypesRootsForAccordions,
      inputsFingerprint: parentAccordionInputsFingerprint,
      enabled: parentAccordionsEnabled,
    },
  )

  const parentAccordionFieldsFlat = computed(() => {
    const out: ObjectInventoryField[] = []
    for (const row of parentAccordions.value) {
      out.push(...row.fields)
    }
    return out
  })

  const parentAccordionFieldIds = computed(() =>
    parentAccordionFieldIdSet(parentAccordionFieldsFlat.value),
  )

  const ownFieldMetadata = computed(() => {
    const normalized = normalizedOwnFieldMetadata.value
    if (normalized) return normalized
    return mergeFieldMetadata(
      element.value.invent_object_type?.fields,
      objectTypeConfig.value.fields,
      paletteQuery.data.value?.data?.fields as ObjectInventoryField[] | undefined,
    )
  })

  const runtimeFieldMetadata = computed(() =>
    mergeFieldMetadata(ownFieldMetadata.value, parentAccordionFieldsFlat.value),
  )

  const syntheticParentObjectNameFields = computed(() => {
    const meta = runtimeFieldMetadata.value
    return fieldGroupsRef.value
      .filter((g) => g.type === 'PARENT')
      .map((g) =>
        syntheticParentObjectNameInventoryField(
          g,
          meta.filter((f) => Number(f.group) === Number(g.id)),
        ),
      )
  })

  const syntheticListCatalogNameFields = computed(() => {
    const rows: ObjectInventoryField[] = []
    const seen = new Set<number>()
    for (const acc of parentAccordions.value) {
      if (acc.isLoading || acc.isError) continue
      const tid = Number(acc.objectTypeId)
      if (!Number.isFinite(tid) || tid <= 0) continue
      if (seen.has(tid)) continue
      seen.add(tid)
      rows.push(
        syntheticListCatalogNameInventoryField(
          tid,
          (acc.linkedCatalogLabel ?? '').trim() || 'Наименование типа',
        ),
      )
    }
    return rows
  })

  const layoutFieldMetadata = computed(() =>
    mergeFieldMetadata(
      runtimeFieldMetadata.value,
      syntheticParentObjectNameFields.value,
      syntheticListCatalogNameFields.value,
    ),
  )

  const supportedFieldMetadata = computed(() =>
    filterRuntimeOwnCatalogFields(ownFieldMetadata.value, parentAccordionFieldIds.value),
  )
  const isRuntimeMetadataReady = computed(() => {
    if (!elementId.value) return false
    if (passportFieldsQuery.isLoading.value || passportFieldsQuery.isFetching.value) return false
    if (useNormalizedPassport.value) return true
    if (!objectTypeId.value) return false
    if (!objectTypeQuery.data.value?.data) return false
    if (!paletteQuery.data.value?.data) return false
    if (needsParentAccordionMetadata.value && parentAccordionsLoading.value) return false
    return true
  })

  // Same `object_list_id` can represent different versions; the guard below used to skip remapping
  // whenever id matched, so changing `status_date` refetched correct API data but left stale `fieldValues`.
  watch(
    () => filter.value.status_date,
    () => {
      initialSnapshot.value = null
      resetPseudosqlState()
    },
    { flush: 'sync' },
  )
  watch(
    () => elementId.value,
    () => {
      initialSnapshot.value = null
      resetPseudosqlState()
    },
    { flush: 'sync' },
  )

  let lastLoadedElement: object | undefined

  watch(
    () =>
      [
        elementQuery.data.value?.data?.[0],
        isRuntimeMetadataReady.value,
        normalizedPassportPayload.value,
        useNormalizedPassport.value,
      ] as const,
    ([loadedElement, metadataReady, normalizedPayload, normalized]) => {
      if (!loadedElement) return

      const nextElement = mergeLoadedObjectPassportElement(
        createEmptyObjectPassportElement(),
        loadedElement as Partial<ObjectPassportElement>,
      )

      if (loadedElement !== lastLoadedElement) {
        element.value = nextElement
        lastLoadedElement = loadedElement
      }

      if (!metadataReady || (initialSnapshot.value && element.value.id === nextElement.id)) return

      if (normalized && normalizedPayload) {
        fieldValues.value = buildRuntimeFieldValueMapFromObjectPassport(
          supportedFieldMetadata.value,
          normalizedPayload,
          'edit',
        )
      } else {
        fieldValues.value = createRuntimeFieldValueMap(
          supportedFieldMetadata.value,
          toRuntimeFieldArray(nextElement.fields),
        )
      }
      initialSnapshot.value = cloneObjectPassportSnapshot(
        nextElement,
        fieldValues.value,
        supportedFieldMetadata.value,
      )
    },
    { immediate: true },
  )

  const layout = computed(() =>
    buildRuntimeLayout({
      passport_grid_template: passportGridTemplateRef.value,
      field_groups: fieldGroupsRef.value as never[] | undefined,
      fieldMetadata: layoutFieldMetadata.value,
    }),
  )

  const isLoading = computed(
    () =>
      elementQuery.isLoading.value ||
      elementQuery.isFetching.value ||
      passportFieldsQuery.isLoading.value ||
      passportFieldsQuery.isFetching.value ||
      (!useNormalizedPassport.value &&
        Boolean(objectTypeId.value) &&
        (objectTypeQuery.isLoading.value ||
          objectTypeQuery.isFetching.value ||
          paletteQuery.isPending.value ||
          paletteQuery.isFetching.value ||
          (needsParentAccordionMetadata.value && parentAccordionsLoading.value))),
  )

  const hasError = computed(
    () =>
      elementQuery.isError.value ||
      (!useNormalizedPassport.value &&
        (objectTypeQuery.isError.value || paletteQuery.isError.value)),
  )

  const error = computed(
    () =>
      elementQuery.error.value ??
      (!useNormalizedPassport.value
        ? (objectTypeQuery.error.value ?? paletteQuery.error.value)
        : null) ??
      null,
  )

  const pendingBindings = usePendingTableBindings()
  provide(PENDING_BINDINGS_KEY, pendingBindings)
  const pendingFieldLinks = usePendingFieldLinks()
  provide(PENDING_FIELD_LINKS_KEY, pendingFieldLinks)

  const isDirty = computed(
    () =>
      hasObjectPassportChanges(
        initialSnapshot.value,
        element.value,
        fieldValues.value,
        supportedFieldMetadata.value,
      ) || pendingBindings.hasPending.value || pendingFieldLinks.hasPending.value,
  )

  const { send, isPending: isSavingLegacy } = useSendObjectList()
  const { mutateAsync: saveNormalizedFieldValues, isPending: isSavingNormalized } =
    useSaveObjectPassportFields()
  const isSaving = computed(() => isSavingLegacy.value || isSavingNormalized.value)

  const linkedDisplayByGridId = ref<Record<string, string>>({})
  const linkedValuesLoading = ref(false)
  let linkedFetchGeneration = 0

  const {
    pseudosqlValues,
    recompute: recomputePseudosql,
    reset: resetPseudosqlValues,
  } = usePassportPseudosqlRecompute({
    elementId,
    typeId: objectTypeId,
    fieldValues,
  })

  watch(
    () => {
      if (!isRuntimeMetadataReady.value || !objectId.value) return null
      if (!objectTypeId.value) {
        const allItems = layout.value.tabs.flatMap((tab) =>
          (tab.sections ?? []).flatMap((sec) => sec.items ?? []),
        )
        const hasRefAnyCells = allItems.some((it) => String(it.layoutItem.id).startsWith('ref:'))
        if (!hasRefAnyCells) return null
      }

      const sourceIds = collectLinkedSourceFieldIds(
        layout.value.tabs,
        objectTypeId.value,
        layoutFieldMetadata.value,
      )
      const sourceValues = sourceIds.map((id) => fieldValues.value[id] ?? '')
      const linkedCellIds = layout.value.tabs
        .flatMap((tab) => (tab.sections ?? []).flatMap((sec) => sec.items ?? []))
        .filter((it) => it.kind === 'field')
        .map((it) => String(it.layoutItem.id))
        .filter((id) => id.startsWith('ref:') || id.startsWith('parent-acc-'))
        .sort()
        .join(',')

      return [
        objectId.value,
        String(objectTypeId.value),
        filter.value.status_date ?? '',
        String(element.value.id ?? ''),
        String(element.value.parent_object?.name ?? '').trim(),
        linkedCellIds,
        ...sourceIds,
        ...sourceValues,
      ].join('\0')
    },
    async (watchKey) => {
      if (watchKey == null) {
        linkedDisplayByGridId.value = {}
        linkedValuesLoading.value = false
        return
      }
      const gen = ++linkedFetchGeneration
      linkedValuesLoading.value = true
      try {
        const next = await buildLinkedPassportDisplayMapForRuntime(
          http,
          filter.value,
          fieldValues.value,
          layout.value.tabs,
          objectTypeId.value,
          layoutFieldMetadata.value,
          objectId.value,
          String(element.value.parent_object?.name ?? '').trim(),
        )
        if (gen !== linkedFetchGeneration) return
        linkedDisplayByGridId.value = next
      } finally {
        if (gen === linkedFetchGeneration) linkedValuesLoading.value = false
      }
    },
    { immediate: true },
  )

  let recomputeTriggered = false

  /**
   * Сброс кеша pseudosql-подписей при смене версии/объекта: иначе устаревший
   * value_result от прошлого recompute перекрывает срез версии из GET passport.
   */
  const resetPseudosqlState = () => {
    recomputeTriggered = false
    resetPseudosqlValues()
  }

  watch(
    [fieldValues, useNormalizedPassport, normalizedPassportPayload] as const,
    ([fv, normalized, payload]) => {
      // Пока метаданные не готовы (refetch версии), fieldValues ещё от прошлого
      // среза — recompute с ними даст устаревшие подписи и съест recomputeTriggered.
      // elementQuery тоже: fieldValues пересобирается только когда map-паспорт пришёл.
      if (!isRuntimeMetadataReady.value) return
      if (elementQuery.isLoading.value || elementQuery.isFetching.value) return
      if (recomputeTriggered || !normalized || !payload || !Object.keys(fv).length) return
      const hasCatalogFieldWithDefault = payload.fields.some(
        (f) => f.field_type === 10 && (f.default_value ?? '') !== '',
      )
      if (!hasCatalogFieldWithDefault) return
      recomputeTriggered = true
      recomputePseudosql()
    },
    { immediate: true },
  )

  const workflowEnabled = computed(() =>
    Boolean(element.value?.invent_object_type?.properties?.is_must_accept),
  )

  const save = async (verDate?: string) => {
    if (!element.value.id) return false

    const connectionId =
      (element.value.connection_object_list_id as number | null | undefined) ?? null
    const normalized = useNormalizedPassport.value
    const envelopeFields = normalized ? ({} as RuntimeFieldValueMap) : fieldValues.value
    const envelopeFieldMetadata = normalized ? [] : supportedFieldMetadata.value
    const effectiveVerDate = isExplicitVerDate(verDate) ? String(verDate) : undefined

    if (normalized) {
      try {
        const valuesPayload = buildObjectPassportFieldValuesPayload(
          fieldValues.value,
          supportedFieldMetadata.value,
        )
        const fieldLinkOverlay = pendingFieldLinks.buildValuesOverlay()
        const merged = fieldLinkOverlay.length
          ? mergeFieldLinkPatches(valuesPayload, fieldLinkOverlay)
          : valuesPayload
        // Имя элемента отправляем, только если оно изменилось относительно снапшота.
        const changedName =
          initialSnapshot.value && element.value.name !== initialSnapshot.value.name
            ? element.value.name
            : undefined
        await saveNormalizedFieldValues({
          objectListId: Number(element.value.id),
          values: merged,
          name: changedName,
          verDate: effectiveVerDate,
        })
      } catch (error: any) {
        addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
        return false
      }
      pendingBindings.clear()
      pendingFieldLinks.clear()
      initialSnapshot.value = cloneObjectPassportSnapshot(
        element.value,
        fieldValues.value,
        supportedFieldMetadata.value,
      )
      // GET /objects/:id/passport использует status_date; PUT шлёт ver_date того же среза.
      if (effectiveVerDate) {
        filter.value.status_date = effectiveVerDate
      }
      await passportFieldsQuery.refetch()
      return true
    }

    const payload = buildObjectPassportSavePayload(
      {
        id: element.value.id,
        name: element.value.name,
        is_projecting: Boolean(element.value.is_projecting),
        connection_object_list_id: connectionId,
        connection_object_id: connectionId,
        fields: envelopeFields,
        files: element.value.files,
        comment: String(element.value.comment ?? ''),
        relation_links: (element.value.relation_links as unknown[]) ?? [],
        is_original: !workflowEnabled.value,
      },
      envelopeFieldMetadata,
    )

    const bindingsDelta = pendingBindings.buildDelta()
    if (bindingsDelta) {
      payload.table_bindings = bindingsDelta
    }

    const isSaved = await send(payload, effectiveVerDate)
    if (isSaved) {
      // GET /inventory/objects/passport uses `status_date`; PUT uses `ver_date` for the same slice.
      if (effectiveVerDate) {
        filter.value.status_date = effectiveVerDate
      }
      if (bindingsDelta) {
        pendingBindings.clear()
      }
      if (pendingFieldLinks.hasPending.value) {
        pendingFieldLinks.clear()
      }
      initialSnapshot.value = cloneObjectPassportSnapshot(
        element.value,
        fieldValues.value,
        supportedFieldMetadata.value,
      )
      if (normalized) {
        await passportFieldsQuery.refetch()
      }
    }
    return isSaved
  }

  const refetch = async () => {
    initialSnapshot.value = null
    const elementResult = await elementQuery.refetch()
    await passportFieldsQuery.refetch()
    if (!objectTypeId.value) return elementResult
    await Promise.all([objectTypeQuery.refetch(), paletteQuery.refetch()])
    return elementResult
  }

  provide('updateLinkedFieldValue', (fieldId: string, value: string) => {
    fieldValues.value = { ...fieldValues.value, [fieldId]: value }
  })

  return {
    isLoading,
    hasError,
    error,
    isSaving,
    isDirty,
    layout,
    element,
    statusDates: computed(() => element.value.status_dates ?? []),
    fieldValues,
    /** Tab / LIST `field_groups` (or `groups`) for resolving LIST subtype when grid omits `inventory_type`. */
    fieldGroups: fieldGroupsRef,
    /** Merged inventory field metadata (excludes synthetic parent-name cells; save/dirty use this). */
    fieldMetadata: supportedFieldMetadata,
    /** Полные метаданные раскладки: нужны readonly-ячейкам справочников и связанных объектов. */
    layoutFieldMetadata,
    /** «Справочники» cells: plain text from linked passport rows (key = grid `layoutItem.id`). */
    linkedDisplayByGridId: computed(() => linkedDisplayByGridId.value),
    linkedValuesLoading: computed(() => linkedValuesLoading.value),
    useNormalizedPassport,
    normalizedPassportPayload,
    pseudosqlValues,
    recomputePseudosql,
    save,
    refetch,
  }
}
